import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import pokeBowl from "../../assets/images/landing/bowl-poke.png";
import canGreen from "../../assets/images/landing/can-green.png";
import cardsWithTransactions from "../../assets/images/landing/cards-with-transactions.png";
import sandwich from "../../assets/images/landing/sandwich.png";
import star from "../../assets/images/landing/star.png";
import onboardingImage from "../../assets/images/onboarding/onboarding-steps.png";
import logoCobee from "../../assets/logo-cobee.svg";
import { useAuth } from "../../auth";
import Button from "../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../components/button/Button.types";
import { Footer } from "../../components/footer";
import { useMetrics } from "../../metrics";
import { ROUTES } from "../../routes";

const SignIn = () => {
  const { t: translate } = useTranslation();
  const { loginWithRedirect } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useMetrics();

  const handleOnSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formId: string
  ) => {
    const buttonPosition = formId === "email-first" ? "First" : "Second";
    trackEvent(`Activate Account ${buttonPosition} Button Clicked`);
    event.preventDefault();
    navigate({
      pathname: ROUTES.ADD_EMAIL,
      search: `?email=${
        (document.getElementById(formId) as HTMLInputElement).value
      }`,
    });
  };

  useEffect(() => {
    trackEvent("Viewed SignIn Page");
  }, []);

  return (
    <div className="flex flex-col items-center h-full w-full">
      <header className="flex w-full items-center justify-between h-16 py-5 px-20">
        <img src={logoCobee} alt={translate("common.logo")} />
        <div className="w[200px]">
          <Button
            onClick={() => {
              loginWithRedirect();
              trackEvent("Header Login Button Clicked");
            }}
            text={translate("sign-in.login.button")}
            variant={ButtonVariant.OUTLINE}
            size={ButtonSize.SMALL}
          />
        </div>
      </header>
      <div className="flex justify-center w-full bg-gradient-to-b from-white to-[#EBE4FC]">
        <div className="flex py-[120px] px-[88px] gap-36 w-[1440px] w-max-[1440px]">
          <div className="flex flex-col max-w-2xl gap-10">
            <div className="flex flex-col gap-8 ">
              <div className="flex flex-col gap-4">
                <h1 className="font-bold text-6xl leading-[60px] text-gray-900">
                  {translate("sign-in.activation-section.title")}
                </h1>
                <p className="font-normal text-lg leading-7 text-gray-700">
                  {translate("sign-in.activation-section.description")}
                </p>
              </div>
              <form
                className="flex flex-col w-80 justify-center h-10 gap-3 lg:flex-row lg:w-full lg:justify-start"
                onSubmit={(event) => handleOnSubmit(event, "email-first")}
              >
                <input
                  required
                  placeholder={translate("sign-in.field.placeholder")}
                  type="email"
                  id="email-first"
                  className="h-12 w-80 border border-gray-200 border-solid rounded-xl px-2 py-4 focus:ring-transparent focus:outline-none"
                />
                <div className="w-48">
                  <Button type="submit" text={translate("sign-in.button")} />
                </div>
              </form>
            </div>
            <div className="flex h-7 w-full gap-1">
              <p className="text-gray-700 text-sm leading-7">
                {translate("sign-in.active-account-question.description")}
              </p>
              <a
                className="text-sm leading-7 text-secondary-dark font-medium cursor-pointer"
                onClick={() => loginWithRedirect()}
                role="link"
                tabIndex={0}
              >
                {translate("sign-in.active-account-question.link")}
              </a>
            </div>
          </div>
          <div className="justify-center items-center hidden lg:flex">
            <img
              src={onboardingImage}
              alt=""
              className="max-w-[344px] max-h-[315px] self-center object-cover"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center w-full bg-secondary-darkPressed">
        <div className="px-[112px] pt-[104px] pb-[152px]">
          <div className="flex flex-col gap-20">
            <div className="flex justify-between">
              <h2 className="max-w-2xl text-white text-[48px] font-extrabold leading-none">
                {translate("sign-in.steps-section.title")}
              </h2>
            </div>
            <div className="flex gap-6 flex-wrap">
              <div className="flex flex-col w-72">
                <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full bg-secondary-light mb-6">
                  <svg
                    data-testid="check-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="Icons/check">
                      <path
                        id="Vector"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.51957 17.8946 9.26522 18 9 18C8.73478 18 8.48043 17.8946 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z"
                        fill="#0F2365"
                      />
                    </g>
                  </svg>
                </div>
                <p className="text-white text-lg font-extrabold leading-7 mb-1">
                  {translate("sign-in.steps-section.step1.title")}
                </p>
                <p className="text-gray-100 text-base font-normal leading-6">
                  {translate("sign-in.steps-section.step1.description")}
                </p>
              </div>
              <div className="flex flex-col w-72">
                <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full bg-secondary-light mb-6">
                  <svg
                    data-testid="check-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="Icons/check">
                      <path
                        id="Vector"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.51957 17.8946 9.26522 18 9 18C8.73478 18 8.48043 17.8946 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z"
                        fill="#0F2365"
                      />
                    </g>
                  </svg>
                </div>
                <p className="text-white text-lg font-extrabold leading-7 mb-1">
                  {translate("sign-in.steps-section.step2.title")}
                </p>
                <p className="text-gray-100 text-base font-normal leading-6">
                  {translate("sign-in.steps-section.step2.description")}
                </p>
              </div>
              <div className="flex flex-col w-72">
                <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full bg-secondary-light mb-6">
                  <svg
                    data-testid="check-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="Icons/check">
                      <path
                        id="Vector"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.51957 17.8946 9.26522 18 9 18C8.73478 18 8.48043 17.8946 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z"
                        fill="#0F2365"
                      />
                    </g>
                  </svg>
                </div>
                <p className="text-white text-lg font-extrabold leading-7 mb-1">
                  {translate("sign-in.steps-section.step3.title")}
                </p>
                <p className="text-gray-100 text-base font-normal leading-6">
                  {translate("sign-in.steps-section.step3.description")}
                </p>
              </div>
              <div className="flex flex-col w-72">
                <div className="flex w-8 h-8 p-1 justify-center items-center rounded-full bg-secondary-light mb-6">
                  <svg
                    data-testid="check-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="Icons/check">
                      <path
                        id="Vector"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M20.7071 5.29289C21.0976 5.68342 21.0976 6.31658 20.7071 6.70711L9.70711 17.7071C9.51957 17.8946 9.26522 18 9 18C8.73478 18 8.48043 17.8946 8.29289 17.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929C3.68342 10.9024 4.31658 10.9024 4.70711 11.2929L9 15.5858L19.2929 5.29289C19.6834 4.90237 20.3166 4.90237 20.7071 5.29289Z"
                        fill="#0F2365"
                      />
                    </g>
                  </svg>
                </div>
                <p className="text-white text-lg font-extrabold leading-7 mb-1">
                  {translate("sign-in.steps-section.step4.title")}
                </p>
                <p className="text-gray-100 text-base font-normal leading-6">
                  {translate("sign-in.steps-section.step4.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center w-full">
        <div className="flex py-[112px] px-[80px] gap-16">
          <img
            src={cardsWithTransactions}
            alt=""
            className="max-w-[344px] max-h-[315px] self-center object-cover hidden lg:flex"
          />

          <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4 max-w-2xl">
                <p className="font-semibold text-base text-secondary-main">
                  {translate("sign-in.meal-context-section.tag")}
                </p>
                <h2 className="font-extrabold text-4xl">
                  {translate("sign-in.meal-context-section.title")}
                </h2>
                <h3 className="font-normal text-lg leading-7 text-gray-700">
                  {translate("sign-in.meal-context-section.description")}
                </h3>
              </div>
            </div>
            <div className="flex flex-col w-full gap-6">
              <div className="flex flex-row">
                <img src={pokeBowl} className="w-[48px] h-[48px] mr-3" />
                <div>
                  <p className="font-semibold text-xl leading[30px]">
                    {translate("sign-in.meal-context-section.advise1.title")}
                  </p>
                  <p className="font-normal text-base text-gray-700">
                    {translate(
                      "sign-in.meal-context-section.advise1.description"
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-row">
                <img src={canGreen} className="w-[48px] h-[48px] mr-3" />
                <div>
                  <p className="font-semibold text-xl leading[30px]">
                    {translate("sign-in.meal-context-section.advise2.title")}
                  </p>
                  <p className="font-normal text-base text-gray-700">
                    {translate(
                      "sign-in.meal-context-section.advise2.description"
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-row">
                <img src={sandwich} className="w-[48px] h-[48px] mr-3" />
                <div>
                  <p className="font-semibold text-xl leading[30px]">
                    {translate("sign-in.meal-context-section.advise3.title")}
                  </p>
                  <p className="font-normal text-base text-gray-700">
                    {translate(
                      "sign-in.meal-context-section.advise3.description"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center w-full bg-secondary-darkPressed">
        <div className="py-[112px] px-[80px]">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-center font-extrabold text-5xl leading-[58px] text-white">
                {translate("sign-in.activation2-section.title")}
              </h2>
              <p className="text-center text-gray-300 font-normal text-lg max-w-xl">
                {translate("sign-in.activation2-section.description")}
              </p>
            </div>
            <form
              className="flex flex-col justify-center gap-3 lg:flex-row"
              onSubmit={(event) => handleOnSubmit(event, "email-second")}
            >
              <input
                required
                placeholder={translate("sign-in.field.placeholder")}
                type="email"
                id="email-second"
                className="h-12 w-80 border border-gray-200 border-solid rounded-xl px-2 py-4 focus:ring-transparent focus:outline-none"
              />
              <div className={"w[200px]"}>
                <Button type="submit" text={translate("sign-in.button")} />
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center py-20 px-28 gap-11 w-full">
        <div className="flex flex-col max-w-2xl gap-4">
          <h2 className="text-gray-900 text-center text-4xl font-extrabold leading-10">
            {translate("sign-in.numbers-section.title")}
          </h2>
          <p className="text-gray-700 text-center text-lg font-normal leading-7">
            {translate("sign-in.number-section.description")}
          </p>
        </div>
        <div className="flex flex-col content-between lg:flex-row">
          <div className="flex flex-col gap-4 items-center max-w-[404px] p-7">
            <p className="text-secondary-main text-center text-6xl font-extrabold leading-none">
              {translate("sign-in.numbers-section.number1.title")}
            </p>
            <p className="text-gray-900 text-center text-base font-medium leading-6 w-[240px]">
              {translate("sign-in.numbers-section.number1.description")}
            </p>
          </div>
          <div className="flex flex-col gap-4 items-center border-gray-300 max-w-[404px] p-7 lg:border-r lg:border-l">
            <p className="text-secondary-main text-center text-6xl font-extrabold leading-none">
              {translate("sign-in.numbers-section.number2.title")}
            </p>
            <p className="text-gray-900 text-center text-base font-medium leading-6 w-[240px]">
              {translate("sign-in.numbers-section.number2.description")}
            </p>
          </div>
          <div className="relative flex flex-col gap-4 items-center max-w-[404px] p-7">
            <img
              className="absolute right-[50px] top-[-35px] w-[82px] h-[82px]"
              src={star}
              alt=""
              data-testid="star-image"
            />
            <p className="text-secondary-main text-center text-6xl font-extrabold leading-none">
              {translate("sign-in.numbers-section.number3.title")}
            </p>
            <p className="text-gray-900 text-center text-base font-medium leading-6 w-[240px]">
              {translate("sign-in.numbers-section.number3.description")}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export { SignIn };
